import React, { useEffect, useMemo, useState } from "react";
import { Drug } from "../types";
import EGfrApp from "../../eGfr/EGfrApp";
import { KidneySection, KidneyOption } from "./KidneySection";

import radioButtonOnFill from "../../../assets/radio-button-on-fill.svg";
import radioButtonOn from "../../../assets/radio-button-on.svg";

type Trimester = "trimester 1" | "trimester 2" | "trimester 3" | null;
type DialysisType = "hemodialysis" | "capd" | "crrt" | null;

export interface SensitivitiesAnswers {
	kidneyIssue: boolean;
	onDialysis: boolean;
	dialysisType: DialysisType;
	cctValue: number | null;
	pregnant: boolean;
	trimester: Trimester;
	nursing: boolean;
	hasAllergies: boolean;
	selectedAllergies: string[];
}

interface Props {
	drugs: Drug[];
	/** Drug IDs relevant to the selected diagnosis */
	relevantDrugIds: string[];
	onDone: (answers: SensitivitiesAnswers) => void;
}

export function SensitivitiesStep({ drugs, relevantDrugIds, onDone }: Props) {

	const [kidneyIssue, setKidneyIssue] = useState(false);
	const [kidneyOption, setKidneyOption] = useState<KidneyOption>(null);
	const [cctValue, setCctValue] = useState("");
	const [isEgfrModalOpen, setIsEgfrModalOpen] = useState(false);
	const [isMobileEgfrDrawer, setIsMobileEgfrDrawer] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const mediaQuery = window.matchMedia("(max-width: 767px)");

		const updateIsMobile = (event?: MediaQueryListEvent) => {
			setIsMobileEgfrDrawer(event ? event.matches : mediaQuery.matches);
		};

		updateIsMobile();

		if (typeof mediaQuery.addEventListener === "function") {
			mediaQuery.addEventListener("change", updateIsMobile);
			return () => mediaQuery.removeEventListener("change", updateIsMobile);
		}

		mediaQuery.addListener(updateIsMobile);
		return () => mediaQuery.removeListener(updateIsMobile);
	}, []);

	const [pregnant, setPregnant] = useState(false);
	const [trimester, setTrimester] = useState<Trimester>(null);

	const [nursing, setNursing] = useState(false);

	const [hasAllergies, setHasAllergies] = useState(false);
	const [selectedAllergies, setSelectedAllergies] = useState<string[]>([]);

	const trimesterOptions: { id: Exclude<Trimester, null>; label: string }[] = [
		{ id: "trimester 1", label: "שליש ראשון" },
		{ id: "trimester 2", label: "שליש שני" },
		{ id: "trimester 3", label: "שליש שלישי" },
	];

	const parsedCctValue = useMemo(() => {
		if (cctValue.trim() === "") return null;

		const value = Number(cctValue);
		if (!Number.isFinite(value) || value < 0) return null;

		return value;
	}, [cctValue]);

	const allergyDrugs = relevantDrugIds
		.map((id) => drugs.find((d) => d.id === id))
		.filter(Boolean) as Drug[];

	const handleKidneyToggle = () => {
		const next = !kidneyIssue;
		setKidneyIssue(next);

		if (!next) {
			setKidneyOption(null);
			setCctValue("");
			setIsEgfrModalOpen(false);
		}
	};

	const handleKidneyOptionSelect = (option: Exclude<KidneyOption, null>) => {
		setKidneyOption(option);
		if (option !== "cct") {
			setCctValue("");
		}
	};

	const handleOpenEgfrCalculator = () => {
		setIsEgfrModalOpen(true);
	};

	const handleEgfrApply = (value: string) => {
		setCctValue(value);
		setIsEgfrModalOpen(false);
	};

	const handlePregnantToggle = () => {
		const next = !pregnant;
		setPregnant(next);

		if (!next) {
			setTrimester(null);
		}
	};

	const handleNursingToggle = () => {
		setNursing((prev) => !prev);
	};

	const handleHasAllergiesToggle = () => {
		const next = !hasAllergies;
		setHasAllergies(next);

		if (!next) {
			setSelectedAllergies([]);
		}
	};

	const handleAllergyToggle = (id: string) => {
		setSelectedAllergies((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		);
	};

	const isDialysisOption = kidneyOption === "hemodialysis" || kidneyOption === "capd" || kidneyOption === "crrt";
	const isCctOption = kidneyOption === "cct";

	const isKidneySectionValid = (() => {
		if (!kidneyIssue) return true;
		if (isDialysisOption) return true;
		if (isCctOption) return parsedCctValue !== null;
		return false;
	})();

	const isPregnancySectionValid = !pregnant || trimester !== null;
	const canContinue = isKidneySectionValid && isPregnancySectionValid;

	const kidneyPayload = {
		onDialysis: kidneyIssue ? isDialysisOption : false,
		dialysisType: kidneyIssue && isDialysisOption ? (kidneyOption as DialysisType) : null,
		cctValue: kidneyIssue && isCctOption ? parsedCctValue : null,
	};

	const handleContinue = () => {
		onDone({
			kidneyIssue,
			...kidneyPayload,
			pregnant,
			trimester: pregnant ? trimester : null,
			nursing,
			hasAllergies,
			selectedAllergies,
		});
	};

	const kidneyHint = (() => {
		if (!kidneyIssue) return null;
		if (kidneyOption === null) return "יש לבחור אפשרות כדי להמשיך";
		if (isCctOption && parsedCctValue === null) {
			return "יש להזין ערך CCT תקין כדי להמשיך";
		}
		return null;
	})();

	return (
		<div className="sensitivities-step">
			<KidneySection
				kidneyIssue={kidneyIssue}
				kidneyOption={kidneyOption}
				cctValue={cctValue}
				onToggleKidney={handleKidneyToggle}
				onSelectKidneyOption={handleKidneyOptionSelect}
				onChangeCctValue={setCctValue}
				onOpenEgfrCalculator={handleOpenEgfrCalculator}
				radioButtonOn={radioButtonOn}
				radioButtonOnFill={radioButtonOnFill}
			/>

			<div className="sensitivity-card">
				<label className="sensitivity-toggle-row">
					<div className="sensitivity-toggle-label">
						<span className="sensitivity-icon">🤰</span>
						<span>הריון</span>
					</div>

					<button
						type="button"
						className={`toggle-btn ${pregnant ? "on" : "off"}`}
						onClick={handlePregnantToggle}
						aria-pressed={pregnant}
					>
						<span className="toggle-thumb" />
					</button>
				</label>

				<div className={`sub-options-animate ${pregnant ? "expanded" : ""}`}>
					<div className="sub-options-inner">
						<div className="sub-label">באיזה שליש?</div>
						<div className="trimester-options">
							{trimesterOptions.map((opt) => {
								const isSelected = trimester === opt.id;

								return (
									<button
										key={opt.id}
										type="button"
										className={`trimester-btn ${isSelected ? "selected" : ""}`}
										onClick={() => setTrimester(opt.id)}
									>
										{isSelected && <span className="trim-check">✓</span>}
										{opt.label}
									</button>
								);
							})}
						</div>
					</div>
				</div>
			</div>

			<div className="sensitivity-card">
				<label className="sensitivity-toggle-row">
					<div className="sensitivity-toggle-label">
						<span className="sensitivity-icon">🍼</span>
						<span>הנקה</span>
					</div>

					<button
						type="button"
						className={`toggle-btn ${nursing ? "on" : "off"}`}
						onClick={handleNursingToggle}
						aria-pressed={nursing}
					>
						<span className="toggle-thumb" />
					</button>
				</label>
			</div>

			{allergyDrugs.length > 0 && (
				<div className="sensitivity-card">
					<label className="sensitivity-toggle-row">
						<div className="sensitivity-toggle-label">
							<span className="sensitivity-icon">💊</span>
							<span>רגישות לתרופות</span>
						</div>

						<button
							type="button"
							className={`toggle-btn ${hasAllergies ? "on" : "off"}`}
							onClick={handleHasAllergiesToggle}
							aria-pressed={hasAllergies}
						>
							<span className="toggle-thumb" />
						</button>
					</label>

					<div className={`sub-options-animate ${hasAllergies ? "expanded" : ""}`}>
						<div className="sub-options-inner">
							<div className="sub-label">בחר/י את התרופות שיש אלרגיה אליהן:</div>
							<div className="allergy-pills">
								{allergyDrugs.map((drug) => {
									const isSelected = selectedAllergies.includes(drug.id);

									return (
										<button
											key={drug.id}
											type="button"
											className={`allergy-pill ${isSelected ? "selected" : ""}`}
											onClick={() => handleAllergyToggle(drug.id)}
										>
											{isSelected && <span className="pill-check">✓</span>}
											{drug.displayName}
										</button>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="sensitivities-footer">
				<button
					type="button"
					className="primary-mashlom-btn sensitivities-continue-btn"
					onClick={handleContinue}
					disabled={!canContinue}
				>
					לתוצאות
				</button>

				{kidneyHint && <div className="sensitivities-hint">{kidneyHint}</div>}

				{pregnant && !trimester && (
					<div className="sensitivities-hint">יש לבחור שליש הריון כדי להמשיך</div>
				)}
			</div>

			{isEgfrModalOpen && (
				<div
					onClick={() => setIsEgfrModalOpen(false)}
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(15, 23, 42, 0.55)",
						zIndex: 9999,
						display: "flex",
						alignItems: isMobileEgfrDrawer ? "flex-end" : "center",
						justifyContent: "center",
						padding: isMobileEgfrDrawer ? 0 : 16,
					}}
				>
					<div
						onClick={(e) => e.stopPropagation()}
						style={{
							position: "relative",
							width: isMobileEgfrDrawer
								? "100%"
								: "min(720px, calc(100vw - 32px))",
							height: isMobileEgfrDrawer ? "min(88vh, 820px)" : "auto",
							maxHeight: isMobileEgfrDrawer ? "88vh" : "90vh",
							background: "#F5F7FB",
							overflowY: "auto",
							borderTopLeftRadius: 24,
							borderTopRightRadius: 24,
							borderBottomLeftRadius: isMobileEgfrDrawer ? 0 : 24,
							borderBottomRightRadius: isMobileEgfrDrawer ? 0 : 24,
							boxShadow: isMobileEgfrDrawer
								? "0 -8px 30px rgba(15, 23, 42, 0.18)"
								: "0 24px 80px rgba(15, 23, 42, 0.22)",
						}}
					>
						{isMobileEgfrDrawer && (
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									padding: "12px 0 0 0",
								}}
							>
								<div
									aria-hidden="true"
									style={{
										width: 44,
										height: 5,
										borderRadius: 999,
										background: "#CBD5E1",
									}}
								/>
							</div>
						)}

						<div
							style={{
								position: "sticky",
								top: 0,
								zIndex: 2,
								display: "flex",
								justifyContent: "flex-end",
								alignItems: "center",
								padding: "12px 20px",
								background: "#F5F7FB",
							}}
						>
							<button
								type="button"
								onClick={() => setIsEgfrModalOpen(false)}
								style={{
									border: "1px solid #CBD5E1",
									background: "#fff",
									borderRadius: 12,
									padding: "10px 16px",
									cursor: "pointer",
									font: "inherit",
									flexShrink: 0,
								}}
							>
								סגור
							</button>
						</div>

						<div
							style={{
								maxWidth: 720,
								margin: "0 auto",
								padding: 0,
							}}
						>
							<EGfrApp embedded onApplyResult={handleEgfrApply} />
						</div>
					</div>
				</div>
			)}
		</div>
	);
}